import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MarketingTarotCard } from './MarketingTarotCard';
import { mdBold } from '@/lib/sanitize';
import { ArrowLeft, Sparkles, Star, Loader2, Share2, Copy, Linkedin, Twitter, CheckCircle } from 'lucide-react';

// Card position definitions
const CARD_POSITIONS = [
  { key: 'card1', label: 'Core Force', number: 1 },
  { key: 'card2', label: 'Hidden Influence', number: 2 },
  { key: 'card3', label: 'Recommended Action', number: 3 },
];

export const SharedReading = ({ readingId, onBack }) => {
  const [reading, setReading] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (readingId) {
      fetchReading();
    }
  }, [readingId]);

  // Update meta tags when reading loads
  useEffect(() => {
    if (reading) {
      const cardNames = reading.cards?.map(c => c.name).join(', ') || '';
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/reading/${readingId}/image`;
      
      // Update document title
      document.title = `Marketing Tarot Reading 🔮 - ${cardNames}`;
      
      // Update OG tags dynamically
      const updateMeta = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`) || 
                   document.querySelector(`meta[name="${property}"]`);
        if (meta) {
          meta.setAttribute('content', content);
        }
      };
      
      updateMeta('og:title', `Here's what the marketing gods have predicted 🔮`);
      updateMeta('og:description', `My cards: ${cardNames}. Get your own Marketing Tarot reading!`);
      updateMeta('og:image', imageUrl);
      updateMeta('twitter:title', `Here's what the marketing gods have predicted 🔮`);
      updateMeta('twitter:description', `My cards: ${cardNames}. Get your own Marketing Tarot reading!`);
      updateMeta('twitter:image', imageUrl);
    }
  }, [reading, readingId]);

  const fetchReading = async () => {
    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/reading/${readingId}`);
      
      if (!response.ok) {
        throw new Error('Reading not found');
      }
      
      const data = await response.json();
      setReading(data.reading);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}/api/share/r/${readingId}` 
    : '';
  
  const shareImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/reading/${readingId}/image`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareLinkedIn = () => {
    const cardNames = reading?.cards?.map(c => c.name.replace('The ', '')).join(', ') || '';
    const text = `🔮 Here's what the marketing gods have predicted!\n\nMy cards: ${cardNames}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleShareTwitter = () => {
    const cardNames = reading?.cards?.map(c => c.name.replace('The ', '')).join(', ') || '';
    const text = `🔮 Here's what the marketing gods have predicted!\n\nMy cards: ${cardNames}\n\nGet your own Marketing Tarot reading:`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[100] bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#e89b47] animate-spin mx-auto mb-4" />
          <p className="font-display text-[#e8dcc8]">Loading reading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[100] bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-[#e8dcc8] mb-4">Reading not found</p>
          <Button onClick={onBack} variant="outline" className="border-[#e89b47] text-[#e89b47]">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!reading) return null;

  const context = {
    goal: reading.goal,
    audience: reading.audience,
    industry: reading.industry,
    companySize: reading.company_size,
    segment: reading.segment,
    challenge: reading.challenge,
  };

  const aiReading = reading.reading;
  const spreadCards = reading.cards || [];

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a1628]/90 backdrop-blur-sm border-b border-[#d4c4a8]/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-[#d4c4a8] hover:text-[#e8dcc8] hover:bg-[#d4c4a8]/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Deck
          </Button>
          
          {/* Share buttons */}
          <div className="flex items-center gap-2">
            <Button
              onClick={handleCopyLink}
              variant="ghost"
              size="sm"
              className="text-[#d4c4a8] hover:text-[#e8dcc8]"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">{copied ? 'Copied!' : 'Copy Link'}</span>
            </Button>
            <Button
              onClick={handleShareLinkedIn}
              variant="ghost"
              size="sm"
              className="text-[#d4c4a8] hover:text-[#0A66C2]"
            >
              <Linkedin className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleShareTwitter}
              variant="ghost"
              size="sm"
              className="text-[#d4c4a8] hover:text-[#1DA1F2]"
            >
              <Twitter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        
        {/* Title */}
        <div className="text-center mb-10">
          <p className="font-body text-[#e89b47] text-sm mb-2">Shared Reading</p>
          <h1 className="font-display text-3xl md:text-4xl text-[#e8dcc8] mb-4">
            🔮 The Marketing Tarot Reading
          </h1>
        </div>

        {/* Context Summary */}
        <div className={cn(
          "mb-12 p-6 rounded-xl",
          "bg-[#1e2a4a]/40 border border-[#d4c4a8]/20"
        )}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 font-body text-sm">
            <p><span className="text-[#e89b47]">Marketing Goal:</span> <span className="text-[#e8dcc8]">{context.goal}</span></p>
            <p><span className="text-[#e89b47]">Audience:</span> <span className="text-[#e8dcc8]">{context.audience}</span></p>
            <p><span className="text-[#e89b47]">Industry:</span> <span className="text-[#e8dcc8]">{context.industry}</span></p>
            <p><span className="text-[#e89b47]">Company Size:</span> <span className="text-[#e8dcc8]">{context.companySize}</span></p>
            <p><span className="text-[#e89b47]">Segment:</span> <span className="text-[#e8dcc8]">{context.segment}</span></p>
            <p><span className="text-[#e89b47]">Challenge:</span> <span className="text-[#e8dcc8]">{context.challenge}</span></p>
          </div>
        </div>

        {/* Card Spread Summary */}
        <div className={cn(
          "mb-12 p-6 rounded-xl text-center",
          "bg-[#1e2a4a]/60 border border-[#e89b47]/30"
        )}>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {CARD_POSITIONS.map((pos, index) => (
              <div key={pos.key}>
                <p className="text-[#d4c4a8]/60 text-sm mb-1">Card {pos.number} — {pos.label}:</p>
                <p className="font-display text-[#e89b47] text-lg italic">
                  {spreadCards[index]?.name || '...'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Individual Card Readings */}
        <div className="space-y-12 mb-16">
          {spreadCards.map((card, index) => {
            const position = CARD_POSITIONS[index];
            const interp = aiReading?.[`card${index + 1}`];
            
            return (
              <div key={`${card.name}-${position.label}`}>
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-shrink-0">
                    <MarketingTarotCard
                      card={card}
                      isFlipped={true}
                      size="small"
                      isHoverable={false}
                    />
                  </div>
                  <div>
                    <p className="text-[#d4c4a8]/60 text-sm">Card {position.number}</p>
                    <h2 className="font-display text-2xl text-[#e8dcc8]">
                      ✨ {card.name} — <span className="text-[#e89b47]">{position.label}</span>
                    </h2>
                  </div>
                </div>

                {/* Interpretation */}
                {interp && (
                  <div className={cn(
                    "ml-0 md:ml-36 p-6 rounded-xl",
                    "bg-[#1e2a4a]/40 border border-[#d4c4a8]/10"
                  )}>
                    <p className="font-display text-lg text-[#e8dcc8] mb-4">
                      {interp.title}
                    </p>
                    <p className="font-body text-[#d4c4a8]/80 leading-relaxed mb-4"
                       dangerouslySetInnerHTML={{ __html: mdBold(interp.body, 'text-[#e8dcc8]') }}
                    />
                    <p className="font-body text-[#e89b47] italic border-l-2 border-[#e89b47]/50 pl-4">
                      <strong>Interpretation:</strong> {interp.interpretation}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Overall Reading */}
        {aiReading?.overall && (
          <div className={cn(
            "mb-12 p-8 rounded-xl",
            "bg-gradient-to-br from-[#1e2a4a]/80 to-[#2a1f42]/60",
            "border-2 border-[#e89b47]/40"
          )}>
            <h2 className="font-display text-2xl text-[#e8dcc8] mb-6 flex items-center gap-3">
              <Star className="w-6 h-6 text-[#e89b47]" />
              🌟 The Overall Reading
            </h2>
            
            <p className="font-body text-[#d4c4a8]/90 text-lg mb-6"
               dangerouslySetInnerHTML={{ __html: mdBold(aiReading.overall.intro, 'text-[#e89b47]') }}
            />
            
            <ul className="space-y-2 mb-6">
              {aiReading.overall.bullets?.map((bullet, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[#d4c4a8]/80">
                  <span className="text-[#e89b47] mt-1">•</span>
                  <span dangerouslySetInnerHTML={{ __html: mdBold(bullet, 'text-[#e8dcc8]') }} />
                </li>
              ))}
            </ul>
            
            <p className="font-body text-lg text-[#e8dcc8]"
               dangerouslySetInnerHTML={{ __html: mdBold(aiReading.overall.closing, 'text-[#e89b47]') }}
            />
          </div>
        )}

        {/* Footer */}
        <div className="text-center">
          <p className="font-body text-[#d4c4a8]/50 text-sm">
            Want your own Marketing Tarot Reading?
          </p>
          <Button
            onClick={onBack}
            className="mt-4 bg-[#e89b47] text-[#0a1628] hover:bg-[#e89b47]/90 font-display"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Get Your Reading
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SharedReading;
